import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiSliders, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

const materialGroups = [
  "Cashmere",
  "Cotton",
  "Corduroy",
  "Denim",
  "Leather",
  "Linen",
  "Suede",
  "Wool",
  "Jersey",
];
const sortOptions = [
  "featured",
  "loved",
  "alphabetical",
  "price-low",
  "price-high",
  "newest",
];
const catalogueIsValid = Array.isArray(productData);
const catalogue = catalogueIsValid ? productData : [];

const getMaterialGroup = (material) =>
  materialGroups.find((group) =>
    material.toLowerCase().includes(group.toLowerCase())
  ) || "Other";

function Shop() {
  const { wishlistItems, toggleWishlist, isWishlisted } =
    useContext(CartContext);
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "";
  const searchParam = searchParams.get("search") || "";
  const wishlistParam = searchParams.get("wishlist") === "true";
  const newParam = searchParams.get("new") === "true";
  const sortParam = searchParams.get("sort") || "featured";

  const [categories, setCategories] = useState(() =>
    categoryParam ? categoryParam.split("|").filter(Boolean) : []
  );
  const [sizes, setSizes] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [colors, setColors] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParam);
  const [wishlistOnly, setWishlistOnly] = useState(wishlistParam);
  const [newOnly, setNewOnly] = useState(newParam);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState(
    sortOptions.includes(sortParam) ? sortParam : "featured"
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gridDensity, setGridDensity] = useState(3);

  useEffect(() => {
    setCategories(
      categoryParam ? categoryParam.split("|").filter(Boolean) : []
    );
    setSearchTerm(searchParam);
    setWishlistOnly(wishlistParam);
    setNewOnly(newParam);
    setSortBy(sortOptions.includes(sortParam) ? sortParam : "featured");
  }, [categoryParam, searchParam, wishlistParam, newParam, sortParam]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 240);
    return () => window.clearTimeout(timer);
  }, [
    categories,
    sizes,
    fabrics,
    colors,
    searchTerm,
    wishlistOnly,
    newOnly,
    inStockOnly,
    sortBy,
  ]);

  useEffect(() => {
    const closeFilters = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", closeFilters);
    return () => window.removeEventListener("keydown", closeFilters);
  }, []);

  const filterOptions = useMemo(
    () => ({
      categories: [
        ...new Set(catalogue.map((product) => product.category)),
      ].sort(),
      sizes: [...new Set(catalogue.flatMap((product) => product.sizes))],
      fabrics: [
        ...new Set(
          catalogue.map((product) => getMaterialGroup(product.material))
        ),
      ].sort(),
      colors: [
        ...new Set(catalogue.flatMap((product) => product.colors)),
      ].sort(),
    }),
    []
  );

  const visibleProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = catalogue.filter((product) => {
      const searchableText = [
        product.name,
        product.category,
        product.material,
        ...product.tags,
        ...product.colors,
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!normalizedSearch || searchableText.includes(normalizedSearch)) &&
        (!categories.length || categories.includes(product.category)) &&
        (!sizes.length || sizes.some((size) => product.sizes.includes(size))) &&
        (!fabrics.length ||
          fabrics.includes(getMaterialGroup(product.material))) &&
        (!colors.length ||
          colors.some((color) => product.colors.includes(color))) &&
        (!wishlistOnly || wishlistItems.includes(product.id)) &&
        (!newOnly || product.newArrival) &&
        (!inStockOnly || product.inStock)
      );
    });

    return [...filtered].sort((first, second) => {
      if (sortBy === "price-low") return first.price - second.price;
      if (sortBy === "price-high") return second.price - first.price;
      if (sortBy === "alphabetical")
        return first.name.localeCompare(second.name);
      if (sortBy === "newest")
        return Number(second.newArrival) - Number(first.newArrival);
      if (sortBy === "loved")
        return Number(second.bestseller) - Number(first.bestseller);
      return first.id - second.id;
    });
  }, [
    categories,
    sizes,
    fabrics,
    colors,
    searchTerm,
    wishlistOnly,
    newOnly,
    inStockOnly,
    sortBy,
    wishlistItems,
  ]);

  const activeFilterCount =
    categories.length +
    sizes.length +
    fabrics.length +
    colors.length +
    Number(wishlistOnly) +
    Number(newOnly) +
    Number(inStockOnly);

  const toggleValue = (value, values, setter) => {
    setter(
      values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value]
    );
  };

  const clearFilters = () => {
    setCategories([]);
    setSizes([]);
    setFabrics([]);
    setColors([]);
    setWishlistOnly(false);
    setNewOnly(false);
    setInStockOnly(false);
    setSearchTerm("");
  };

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!catalogueIsValid) {
    return (
      <main className="shop-state" role="alert">
        <h1>We could not load the collection.</h1>
        <p>Please refresh the page or contact client service.</p>
      </main>
    );
  }

  return (
    <main className="shop main">
      {wishlistOnly || newOnly || categories.length || searchTerm ? (
        <header className="shop-heading">
          <p>IRTRADECO / Current release</p>
          <h1>
            {wishlistOnly
              ? "Your wishlist"
              : newOnly
              ? "New arrivals"
              : categories.length === 1
              ? categories[0]
              : "Search"}
          </h1>
          {searchTerm && <span>Results for “{searchTerm}”</span>}
        </header>
      ) : (
        <h1 className="sr-only">Shop all</h1>
      )}

      <div className="shop-toolbar">
        <button
          type="button"
          className="filter-trigger"
          onClick={() => setFiltersOpen(true)}
          aria-expanded={filtersOpen}
        >
          <FiSliders aria-hidden="true" />
          Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
        </button>
        <span className="product-count">{visibleProducts.length} products</span>
        <div className="grid-controls" aria-label="Products per row">
          {[1, 2, 3, 6].map((density) => (
            <button
              key={density}
              type="button"
              className={`density-control-${density} ${
                gridDensity === density ? "is-active" : ""
              }`}
              onClick={() => setGridDensity(density)}
              aria-label={`Show ${density} product${
                density > 1 ? "s" : ""
              } per row`}
              aria-pressed={gridDensity === density}
            >
              <span
                className={`density-icon density-icon-${density}`}
                aria-hidden="true"
              >
                {Array.from({ length: density }).map((_, index) => (
                  <i key={index} />
                ))}
              </span>
            </button>
          ))}
        </div>
        <label className="sort-control">
          <span>Sort by</span>
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="loved">Most loved</option>
            <option value="alphabetical">Alphabetically</option>
            <option value="price-low">Price, low to high</option>
            <option value="price-high">Price, high to low</option>
            <option value="newest">Newest</option>
          </select>
          <FiChevronDown aria-hidden="true" />
        </label>
      </div>

      {loading ? (
        <div
          className={`shop-grid shop-grid-loading shop-grid-density-${gridDensity}`}
          aria-label="Loading collection"
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="shop-skeleton" key={index}>
              <div />
              <span />
              <span />
            </div>
          ))}
        </div>
      ) : visibleProducts.length ? (
        <div className={`shop-grid shop-grid-density-${gridDensity}`}>
          {visibleProducts.map((product) => (
            <article className="shop-card" key={product.id}>
              <div className="shop-card-media">
                <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                  <img
                    src={product.thumbnail}
                    srcSet={`${product.thumbnail} 640w, ${product.image} 1024w`}
                    sizes="(max-width: 699px) 50vw, 34vw"
                    alt={product.name}
                    loading="lazy"
                  />
                  {!product.inStock && (
                    <span className="product-badge">Sold out</span>
                  )}
                  {product.compareAtPrice && (
                    <span className="product-badge sale">Sale</span>
                  )}
                </Link>
                <button
                  type="button"
                  className={`product-heart ${
                    isWishlisted(product.id) ? "is-active" : ""
                  }`}
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={
                    isWishlisted(product.id)
                      ? `Remove ${product.name} from wishlist`
                      : `Add ${product.name} to wishlist`
                  }
                >
                  <FiHeart aria-hidden="true" />
                </button>
              </div>
              <div className="shop-card-info">
                <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                  {product.name}
                </Link>
                <div className="shop-card-price">
                  <span>{formatPrice(product.price)}</span>
                  {product.compareAtPrice && (
                    <del>{formatPrice(product.compareAtPrice)}</del>
                  )}
                </div>
                <small>
                  {product.colors.length}{" "}
                  {product.colors.length === 1 ? "colour" : "colours"} available
                </small>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="shop-empty">
          <h2>No pieces match these filters.</h2>
          <p>Try a different size, colour or material.</p>
          <button type="button" onClick={clearFilters} className="solid-button">
            Clear filters
          </button>
        </div>
      )}

      <div
        className={`filter-backdrop ${filtersOpen ? "is-open" : ""}`}
        onClick={() => setFiltersOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`filter-drawer ${filtersOpen ? "is-open" : ""}`}
        aria-hidden={!filtersOpen}
        aria-label="Collection filters"
      >
        <div className="filter-drawer-header">
          <h2>Filters</h2>
          <button
            type="button"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="filter-scroll">
          <fieldset>
            <legend>Collection</legend>
            <label>
              <input
                type="checkbox"
                checked={newOnly}
                onChange={() => setNewOnly((value) => !value)}
              />
              New arrivals
            </label>
            <label>
              <input
                type="checkbox"
                checked={wishlistOnly}
                onChange={() => setWishlistOnly((value) => !value)}
              />
              Wishlist only
            </label>
            <label>
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={() => setInStockOnly((value) => !value)}
              />
              In stock only
            </label>
          </fieldset>

          {[
            ["Category", filterOptions.categories, categories, setCategories],
            ["Size", filterOptions.sizes, sizes, setSizes],
            ["Fabric", filterOptions.fabrics, fabrics, setFabrics],
          ].map(([label, options, values, setter]) => (
            <fieldset key={label}>
              <legend>{label}</legend>
              {options.map((option) => (
                <label
                  key={option}
                  className={label === "Colour" ? "filter-colour-label" : ""}
                >
                  <input
                    type="checkbox"
                    checked={values.includes(option)}
                    onChange={() => toggleValue(option, values, setter)}
                  />

                  {option}
                </label>
              ))}
            </fieldset>
          ))}
        </div>
        <div className="filter-actions">
          <button type="button" className="filter-clear" onClick={clearFilters}>
            Clear all
          </button>
          <button
            type="button"
            className="solid-button"
            onClick={() => setFiltersOpen(false)}
          >
            View {visibleProducts.length} results
          </button>
        </div>
      </aside>
    </main>
  );
}

export default Shop;
